import { falAngleDown } from 'shared/@generated/icons/falAngleDown';
import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falAngleUp } from 'shared/@generated/icons/falAngleUp';
import { Platform, View } from 'react-native';
import { Props, Breeder, Listing, MediaAsset } from 'shared/@types';
import { useTranslation } from 'react-i18next';
import { H2 } from 'shared/components/title';
import { PageBuilder } from 'shared/library/page-builder';
import { apiRoute } from 'shared/library/api-route';
import { ReactNode, useState } from 'react';
import { Button } from 'shared/components/button';
import { Image } from 'shared/components/image';
import { Text } from 'shared/components/text';

import { useFormatter } from 'shared/library/use-formatter';
import { clsx } from 'clsx';
import { getBreederPetImageAsset, getListingImageAsset } from 'shared/library/get-model-image';
import { getForSalePets } from 'shared/library/pet-roles';
import { useRoute } from 'shared/library/routes/route';
import {
  getPetStatusDotClassName,
  getPetStatusTextClassName,
  getPetStatusVariant,
  isPetAvailable,
} from 'shared/library/pet-status';

interface BreederProfileLittersScreenProps extends Props {
  breeder: Breeder;
  listings: Listing[];
  mediaAssets: MediaAsset[];
}

const BreederProfileLittersScreen = ({ breeder, listings }: BreederProfileLittersScreenProps) => {
  const { t } = useTranslation('screens');

  return (
    <View className={'gap-4 md:gap-6'}>
      <H2 title={t('breeders.show_breeder.our_litters')} marginTop={false} marginBottom={false} />

      {listings.length > 0 ? (
        <View className={'gap-3'}>
          {listings.map((listing) => (
            <ListingCard key={listing.slug} listing={listing} animal={breeder.animal} />
          ))}
        </View>
      ) : (
        <Text className={'text-brand-brown-dark/70 max-w-prose'}>
          {t('breeders.show_breeder.no_litters_added')}
        </Text>
      )}
    </View>
  );
};

const ListingCard = ({
  listing,
  animal,
  actions,
}: {
  listing: Listing;
  animal: string;
  actions?: ReactNode;
}) => {
  const { t } = useTranslation('screens');
  const { formatDate } = useFormatter();
  const route = useRoute();
  const [isExpanded, setIsExpanded] = useState(false);
  const forSalePets = getForSalePets(listing.pets);
  const totalCount = forSalePets.length;
  const petStatusContext = { listingDeleted: !!listing.deleted_at };
  const availableCount = listing.deleted_at
    ? 0
    : forSalePets.filter((pet) => isPetAvailable(pet, petStatusContext)).length;
  const listingHref =
    listing.deleted_at === null ? route('listings.show', { listing: listing.slug }) : null;

  return (
    <View className={'border-brand-brown-dark/15 rounded-lg border bg-white'}>
      <View className={'gap-3 p-3 md:flex-row md:items-center'}>
        <View className={'min-w-0 flex-row items-center gap-3 md:flex-1'}>
          <Image
            src={getListingImageAsset(listing)}
            alt={listing.breed_name}
            containerClassName={'size-20 md:size-24'}
            imageClassName={'rounded-lg object-cover'}
            sizeType={'avatar'}
          />

          <View className={'min-w-0 flex-1'}>
            <Text className={'text-brand-brown-dark text-lg font-bold'}>{listing.breed_name}</Text>
            <Text className={'text-brand-brown-dark mt-0.5'}>
              {t('breeders.show_breeder.born_at', {
                date: formatDate(listing.birth_date),
              })}
            </Text>
            <Text className={'text-brand-brown-dark mt-1 text-sm'}>
              {t('breeders.show_breeder.pets_count', {
                available: availableCount,
                total: totalCount,
              })}
            </Text>
          </View>
        </View>

        <View className={'flex-row items-center gap-3 md:justify-start'}>
          <Button
            onPress={() => setIsExpanded(!isExpanded)}
            text={t(
              isExpanded
                ? 'breeders.show_breeder.hide_details'
                : 'breeders.show_breeder.show_details',
            )}
            icon={isExpanded ? falAngleUp : falAngleDown}
            iconPosition={'right'}
            color={'white'}
            dense={true}
            className={'flex-1 md:flex-none'}
            textClassName={'whitespace-nowrap text-sm'}
          />

          {listingHref && (
            <Button
              href={listingHref}
              text={t('breeders.show_breeder.go_to_listing')}
              icon={falAngleRight}
              iconPosition={'right'}
              color={'white'}
              dense={true}
              className={'flex-1 md:flex-none'}
              textClassName={'whitespace-nowrap text-sm'}
            />
          )}

          {actions}
        </View>
      </View>

      {isExpanded && (
        <View className={'border-brand-brown-dark/15 border-t p-3'}>
          <View
            className={
              Platform.OS === 'web'
                ? 'flex-cols-2 md:flex-cols-4 lg:flex-cols-5 xl:flex-cols-7 flex-row gap-3'
                : 'flex-row flex-wrap gap-3'
            }
          >
            {forSalePets.map((pet) => {
              const statusVariant = getPetStatusVariant(pet, petStatusContext);

              return (
                <View
                  key={pet.uuid}
                  className={'border-brand-brown-dark/15 rounded-lg border bg-white'}
                  style={Platform.OS === 'web' ? undefined : { width: '48%' }}
                >
                  <Image
                    src={getBreederPetImageAsset(pet)}
                    alt={pet.name}
                    containerClassName={'aspect-4/3 w-full'}
                    imageClassName={'rounded-t-lg object-cover'}
                    sizeType={'card'}
                  />

                  <View className={'p-2 md:p-2.5'}>
                    <Text
                      className={'text-brand-brown-dark text-sm font-bold md:text-base'}
                      numberOfLines={1}
                    >
                      {pet.name}
                    </Text>
                    <Text className={'text-brand-brown-dark text-sm md:text-base'}>
                      {t(`common:enums.gender.${pet.gender}_${animal}`)}
                    </Text>

                    <View
                      className={`border-brand-brown-dark/15 mt-2 flex-row items-center self-start rounded-full border bg-white px-1.5 py-0.5`}
                    >
                      <View
                        className={clsx(
                          'mr-1.5 size-1.5 rounded-full',
                          getPetStatusDotClassName(pet, petStatusContext),
                        )}
                      />
                      <Text
                        className={clsx(
                          'text-xs font-semibold',
                          getPetStatusTextClassName(pet, petStatusContext),
                        )}
                      >
                        {statusVariant === 'deleted' && t('components:pet_card.deleted')}
                        {statusVariant === 'deceased' && t('components:pet_card.deceased')}
                        {statusVariant !== 'deleted' &&
                          statusVariant !== 'deceased' &&
                          t(`common:enums.pet_status.${statusVariant}`)}
                      </Text>
                    </View>
                  </View>
                </View>
              );
            })}
          </View>
        </View>
      )}
    </View>
  );
};

BreederProfileLittersScreen.pageBuilder = new PageBuilder<
  BreederProfileLittersScreenProps,
  { breeder: string }
>().request(({ req, params }) =>
  req<{ breeder: Breeder; listings: Listing[]; media_assets: MediaAsset[] }>(
    apiRoute('breeders.show', { breeder: params.breeder }),
    {
      compact: true,
      include: 'listings,media_assets',
    },
  ).then((data) => ({
    breeder: data.breeder,
    listings: data.listings,
    mediaAssets: data.media_assets,
  })),
);

export { BreederProfileLittersScreen, ListingCard };
export type { BreederProfileLittersScreenProps };
